<template>
  <div class="em-spectrum-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>c = λν | 射频至伽马射线$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Wave display -->
          <path :d="wavePath" fill="none" stroke="url(#spectrumGrad)" stroke-width="2" />
          
          <!-- Ruler -->
          <line x1="20" y1="180" x2="380" y2="180" stroke="#888" />
          <g v-for="i in 7" :key="i">
             <line :x1="20 + i*50" y1="180" :x2="20 + i*50" y2="190" stroke="#888" />
             <text :x="10 + i*50" y="205" fill="#888" font-size="7">{{ spectrumLabels[i-1] }}</text>
          </g>

          <!-- Current pointer -->
          <line :x1="posX" y1="40" :x2="posX" y2="180" stroke="#fff" stroke-dasharray="2,2" />
          <circle :cx="posX" cy="180" r="4" fill="#fff" />

          <!-- Details Box -->
          <g transform="translate(250, 40)">
             <rect width="120" height="60" rx="8" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" />
             <text x="10" y="20" fill="#fff" font-size="9">{{ activeLabel }}</text>
             <text x="10" y="35" fill="#888" font-size="7">频率: {{ activeFreq }} Hz</text>
             <text x="10" y="48" fill="#888" font-size="7">应用: {{ activeUsage }}</text>
          </g>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>滑动标尺探索不同波段</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <el-slider v-model="freqLevel" :min="1" :max="400" size="mini"></el-slider>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>电磁波谱按频率（或波长）排列了所有类型的电磁辐射。从波长数公里的**无线电波**，到穿透力极强的**伽马射线**，物理本质完全相同，均为交变电磁场。不同波段与物质的作用方式截然不同：无线电引起电荷共振，红外线引起分子震动，可见光引起电子能级跃迁，而 X 射线则能深入原子内部。这是现代通讯、医疗成像及天文学观测的理论基础。</p>
        </div>
      </div>
    </div>

    <svg height="0" width="0">
      <linearGradient id="spectrumGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" style="stop-color:red" />
        <stop offset="100%" style="stop-color:blue" />
      </linearGradient>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'EMSpectrumViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      freqLevel: 200,
      spectrumLabels: ['无线电', '微波', '红外', '可见光', '紫外', 'X射线', '伽马射线'],
      details: [
        { label: '无线电波', freq: '10³ - 10⁹', usage: '广播、WiFi' },
        { label: '微波', freq: '10⁹ - 10¹²', usage: '雷达、微波炉' },
        { label: '红外线', freq: '10¹² - 10¹⁴', usage: '热成像、遥控器' },
        { label: '可见光', freq: '4×10¹⁴ - 7.5×10¹⁴', usage: '视觉、照明' },
        { label: '紫外线', freq: '10¹⁵ - 10¹⁶', usage: '杀菌、验钞' },
        { label: 'X 射线', freq: '10¹⁶ - 10¹⁹', usage: '医疗 CT、安检' },
        { label: '伽马射线', freq: '> 10¹⁹', usage: '核医学、探伤' }
      ]
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    posX() { return this.freqLevel; },
    activeIndex() {
       const idx = Math.floor((this.freqLevel - 20) / 50);
       return Math.max(0, Math.min(6, idx));
    },
    activeLabel() { return this.details[this.activeIndex].label; },
    activeFreq() { return this.details[this.activeIndex].freq; },
    activeUsage() { return this.details[this.activeIndex].usage; },
    wavePath() {
       const pts = [];
       const baseline = 100;
       for (let x = 0; x <= 400; x += 2) {
          const f = 0.01 + (x / 200) * 0.2;
          const y = baseline + Math.sin(x * f) * 20;
          if (!isNaN(y)) pts.push(`${x},${y.toFixed(1)}`);
       }
       return pts.length > 0 ? "M " + pts.join(" L ") : "";
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/无线电/.test(kp)) this.freqLevel = 40;
      else if (/微波/.test(kp)) this.freqLevel = 90;
      else if (/红外/.test(kp)) this.freqLevel = 140;
      else if (/可见光/.test(kp)) this.freqLevel = 190;
      else if (/紫外/.test(kp)) this.freqLevel = 240;
      else if (/射线|X/.test(kp)) this.freqLevel = 290;
      else if (/伽马/.test(kp)) this.freqLevel = 340;
    }
  }
}
</script>

<style scoped lang="scss">
.em-spectrum-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(255, 255, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
